import { Box, Flex, Stack, Text, useBreakpointValue } from '@chakra-ui/react';
import { setupI18n, type Messages } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createFileRoute, Link, notFound } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { domAnimation, LazyMotion } from 'motion/react';
import { useEffect, useState } from 'react';
import * as z from 'zod';

import { useActiveChatStore } from '#modules/chat/model/activeChatStore';
import { PostPreview, type PostPreviewProps } from '#modules/finder/components/PostPreview';
import { Desktop } from '#modules/finder/features/posts/components/Post/Desktop';
import { MatchAlertsRenewalNotice } from '#modules/finder/features/posts/components/Post/MatchAlertsRenewalNotice';
import { Mobile } from '#modules/finder/features/posts/components/Post/Mobile';
import { SessionDetailsDialog } from '#modules/finder/features/posts/components/Post/SessionDetailsDialog';
import type { PostProps } from '#modules/finder/features/posts/components/Post/types';
import { useMatchAlertsRenewal } from '#modules/finder/features/posts/hooks/useMatchAlertsRenewal';
import { DashboardLayout } from '#modules/user/features/dashboard/components/DashboardLayout';
import { HomePreview } from '#modules/user/features/dashboard/components/HomePreview';
import { Gender } from '#modules/user/features/profile/types';
import { MainLayout } from '#features/layout/components/MainLayout';
import { SessionRoomHost } from '#features/session-room';
import {
  sessionRoomSessionsQueryKey,
  type SessionRoomEntry,
} from '#features/session-room/model/roomSessions';
import { useSessionRoomStore } from '#features/session-room/model/sessionRoomStore';
import { Panel } from '#features/ui';
import { DialogRoot } from '#features/ui/components/Dialog';
import { VoiceRoomProvider } from '#features/voice/components/VoiceRoomProvider';
import { voiceRoomQueryKey } from '#features/voice/model/voice';
import { envConfig } from '#constants/envConfig';

const POST_ID = '11111111-1111-4111-8111-111111111111';
const SESSION_ID = '22222222-2222-4222-8222-222222222222';
const CHAT_ID = '33333333-3333-4333-8333-333333333333';
const GAME_ID = '77777777-7777-4777-8777-777777777777';
const PLATFORM_ID = '88888888-8888-4888-8888-888888888888';
const HOST_ID = '44444444-4444-4444-8444-444444444444';
const MEMBER_ID = '55555555-5555-4555-8555-555555555555';
const GUEST_ID = '66666666-6666-4666-8666-666666666666';
const THIRD_ID = 'aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaaa';
const FUTURE_ID = 'bbbbbbbb-bbbb-4bbb-8bbb-bbbbbbbbbbbb';
const CANDIDATE_ID = '99999999-9999-4999-8999-999999999999';

type Scenario = {
  id: string;
  title: string;
  stats?: 'connected' | 'hidden' | 'account-hidden' | 'private';
  statsPlatform?: 'PLAYSTATION' | 'XBOX';
  statsConnection?: 'loading' | 'error';
  role: 'host' | 'member' | 'guest';
  offset: number;
  durationMinutes?: number;
  mode: 'PLATFORM' | 'DISCORD' | 'IN_GAME' | 'NONE';
  full?: boolean;
  alone?: boolean;
  large?: boolean;
  invited?: boolean;
  archived?: boolean;
  noChat?: boolean;
  future?: boolean;
  futureGuest?: boolean;
  roomHidden?: boolean;
  connected?: boolean;
  discordPending?: boolean;
  anonymous?: boolean;
};

const SCENARIOS: Scenario[] = [
  {
    id: 'host-stats-loading',
    title: 'Статистика · подключение загружается',
    role: 'host',
    offset: -18,
    mode: 'PLATFORM',
    alone: true,
    statsConnection: 'loading',
  },
  {
    id: 'host-stats-error',
    title: 'Статистика · ошибка подключения',
    role: 'host',
    offset: -18,
    mode: 'PLATFORM',
    alone: true,
    statsConnection: 'error',
  },
  {
    id: 'host-stats-private',
    title: 'Статистика · Steam закрыт (Dota)',
    role: 'host',
    offset: -18,
    mode: 'PLATFORM',
    alone: true,
    stats: 'private',
  },
  {
    id: 'host-stats-connected',
    title: 'Статистика · аккаунт подключён',
    role: 'host',
    offset: -18,
    mode: 'PLATFORM',
    alone: true,
    stats: 'connected',
  },
  {
    id: 'host-stats-hidden',
    title: 'Статистика · игра скрыта',
    role: 'host',
    offset: -18,
    mode: 'PLATFORM',
    alone: true,
    stats: 'hidden',
  },
  {
    id: 'host-stats-account-hidden',
    title: 'Статистика · аккаунт скрыт',
    role: 'host',
    offset: -18,
    mode: 'PLATFORM',
    alone: true,
    stats: 'account-hidden',
  },
  {
    id: 'host-stats-playstation',
    title: 'Статистика · PlayStation',
    role: 'host',
    offset: -18,
    mode: 'DISCORD',
    alone: true,
    statsPlatform: 'PLAYSTATION',
  },
  {
    id: 'host-stats-xbox',
    title: 'Статистика · Xbox',
    role: 'host',
    offset: -18,
    mode: 'PLATFORM',
    alone: true,
    statsPlatform: 'XBOX',
  },
  {
    id: 'member-live-future-guest',
    title: 'Участник сейчас · гость позже',
    role: 'member',
    offset: -18,
    mode: 'PLATFORM',
    future: true,
    futureGuest: true,
  },
  { id: 'host-tomorrow', title: 'Автор · завтра', role: 'host', offset: 1440, mode: 'PLATFORM' },
  { id: 'host-soon', title: 'Автор · через 6 минут', role: 'host', offset: 6, mode: 'PLATFORM' },
  {
    id: 'host-large',
    title: 'Автор · большая команда',
    role: 'host',
    offset: -18,
    mode: 'PLATFORM',
    large: true,
  },
  { id: 'host-live', title: 'Автор · идёт', role: 'host', offset: -18, mode: 'PLATFORM' },
  {
    id: 'host-alone',
    title: 'Автор · один',
    role: 'host',
    offset: -18,
    mode: 'PLATFORM',
    alone: true,
  },
  {
    id: 'host-full',
    title: 'Автор · состав полон',
    role: 'host',
    offset: -18,
    mode: 'PLATFORM',
    full: true,
  },
  {
    id: 'host-chat-pending',
    title: 'Автор · чат создаётся',
    role: 'host',
    offset: -18,
    mode: 'PLATFORM',
    noChat: true,
  },
  {
    id: 'host-live-future',
    title: 'Автор · две сессии',
    role: 'host',
    offset: -18,
    mode: 'PLATFORM',
    future: true,
  },
  {
    id: 'host-archived',
    title: 'Автор · архив',
    role: 'host',
    offset: -120,
    mode: 'PLATFORM',
    archived: true,
  },
  {
    id: 'member-tomorrow',
    title: 'Участник · завтра',
    role: 'member',
    offset: 1440,
    mode: 'PLATFORM',
  },
  { id: 'member-live', title: 'Участник · идёт', role: 'member', offset: -18, mode: 'PLATFORM' },
  {
    id: 'member-no-widget',
    title: 'Участник · чат удалён',
    role: 'member',
    offset: -18,
    mode: 'PLATFORM',
    roomHidden: true,
  },
  {
    id: 'member-discord',
    title: 'Участник · Discord',
    role: 'member',
    offset: -18,
    mode: 'DISCORD',
  },
  {
    id: 'member-no-voice',
    title: 'Участник · без голоса',
    role: 'member',
    offset: -18,
    mode: 'NONE',
  },
  { id: 'guest-live', title: 'Гость · есть места', role: 'guest', offset: -18, mode: 'PLATFORM' },
  {
    id: 'guest-full',
    title: 'Гость · состав полон',
    role: 'guest',
    offset: -18,
    mode: 'PLATFORM',
    full: true,
  },
  {
    id: 'guest-anonymous',
    title: 'Без входа · есть места',
    role: 'guest',
    offset: -18,
    mode: 'PLATFORM',
    anonymous: true,
  },
  {
    id: 'invited-live',
    title: 'Приглашён · есть место',
    role: 'guest',
    offset: -18,
    mode: 'PLATFORM',
    invited: true,
  },
  {
    id: 'invited-full',
    title: 'Приглашён · мест нет',
    role: 'guest',
    offset: -18,
    mode: 'PLATFORM',
    invited: true,
    full: true,
  },
  {
    id: 'host-long-date',
    title: 'Автор · длинная дата и длительность',
    role: 'host',
    offset: 172800,
    durationMinutes: 210,
    mode: 'NONE',
  },
  { id: 'host-no-voice', title: 'Автор · без голоса', role: 'host', offset: -18, mode: 'NONE' },
  { id: 'host-in-game', title: 'Автор · голос в игре', role: 'host', offset: -18, mode: 'IN_GAME' },
  {
    id: 'host-no-voice-tomorrow',
    title: 'Автор · без голоса завтра',
    role: 'host',
    offset: 1440,
    mode: 'NONE',
  },
  { id: 'host-discord', title: 'Автор · Discord', role: 'host', offset: -18, mode: 'DISCORD' },
];

SCENARIOS.push(
  {
    id: 'guest-tomorrow',
    title: 'Гость · только запланированная',
    role: 'guest',
    offset: 1440,
    mode: 'PLATFORM',
  },
  {
    id: 'member-voice-occupied',
    title: 'Участник · в голосе есть игрок',
    role: 'member',
    offset: -18,
    mode: 'PLATFORM',
    connected: true,
  },
  {
    id: 'member-discord-pending',
    title: 'Участник · Discord создаётся',
    role: 'member',
    offset: -18,
    mode: 'DISCORD',
    discordPending: true,
  }
);

const searchSchema = z.object({
  state: z.string().optional(),
  previewLocale: z.enum(['ru', 'en']).optional(),
  surface: z.enum(['post', 'card', 'home', 'manage']).optional(),
  activity: z.enum(['playing', 'recent', 'none']).optional(),
  voiceAction: z.enum(['success', 'error', 'loading']).optional(),
  renewal: z.enum(['expired', 'off', 'active', 'error', 'loading']).optional(),
  design: z.enum(['current', 'proposed']).optional(),
});

const minutesFromNow = (minutes: number) => new Date(Date.now() + minutes * 60_000).toISOString();
const noop = () => undefined;
const blockFixtureAction = (event: Event) => {
  const button = event.target instanceof Element ? event.target.closest('button') : null;
  if (
    new URLSearchParams(window.location.search).has('renewal') &&
    button &&
    /^(Bump|Поднять|Not now|Не сейчас)/i.test(button.textContent?.trim() ?? '')
  )
    return;
  if (
    event.target instanceof Element &&
    event.target.closest(
      '[data-testid="session-voice-settings"] button, [data-testid="match-alerts-renewal"] button, [data-testid="home-my-post"] button, [data-testid="home-move"] button, [data-stats-preview-dismiss], [data-scope=collapsible][data-part=trigger], [data-session-compare-controls], [data-session-compare-safe-action], [data-expand-target], [data-scope=dialog][data-part=trigger], [data-scope=dialog][data-part=close-trigger], [data-scope=popover][data-part=close-trigger]'
    )
  ) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
};

const host = {
  memberId: HOST_ID,
  status: 'ACCEPTED',
  userData: { displayName: 'artevard131', avatar: '', socials: [] },
};
const mate = {
  memberId: MEMBER_ID,
  status: 'ACCEPTED',
  userData: { displayName: 'Lily', avatar: '', socials: [] },
};
const third = {
  memberId: THIRD_ID,
  status: 'ACCEPTED',
  userData: { displayName: 'Nova', avatar: '', socials: [] },
};

const fixture = (scenario: Scenario) => {
  const accepted = scenario.large
    ? [
        host,
        mate,
        third,
        ...Array.from({ length: 5 }, (_, index) => ({
          ...mate,
          memberId: `aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaa${index}`,
          userData: { ...mate.userData, displayName: `LongTeammateName${index + 1}` },
        })),
      ]
    : scenario.alone
      ? [host]
      : scenario.full
        ? [host, mate, third]
        : [host, mate];
  const teamLimit = scenario.large ? 10 : 3;
  const members = scenario.invited
    ? [
        ...accepted,
        {
          memberId: GUEST_ID,
          status: 'INVITED',
          userData: { displayName: 'Вы', avatar: '', socials: [] },
        },
      ]
    : accepted;
  const startedAt = minutesFromNow(scenario.offset);
  const expiredAt = minutesFromNow(scenario.offset + (scenario.durationMinutes ?? 60));
  const viewerId =
    scenario.role === 'host' ? HOST_ID : scenario.role === 'member' ? MEMBER_ID : GUEST_ID;
  const session: NonNullable<PostProps['post']['activeSession']> = {
    id: SESSION_ID,
    postId: POST_ID,
    startedAt,
    expiredAt,
    teamLimit,
    status: scenario.archived ? 'COMPLETED' : scenario.offset > 0 ? 'UPCOMING' : 'LIVE',
    discordVoice: scenario.mode === 'DISCORD',
    voiceMode: scenario.mode,
    voiceRoom: {
      connectedCount: scenario.connected ? 1 : 0,
      maxParticipants: teamLimit,
      status: 'OPEN',
    },
    acceptedMembersCount: accepted.length,
    sessionChat:
      scenario.noChat || scenario.roomHidden || scenario.role === 'guest'
        ? null
        : { id: CHAT_ID, unreadCount: 2 },
    members,
  };
  const activeSession = scenario.archived ? null : session;
  const shouldShowRoom = scenario.role !== 'guest' && !scenario.archived;
  const room: SessionRoomEntry = {
    sessionId: SESSION_ID,
    postId: POST_ID,
    hostId: HOST_ID,
    status: scenario.archived ? 'COMPLETED' : scenario.offset > 0 ? 'UPCOMING' : 'LIVE',
    startedAt,
    expiredAt,
    game: {
      id: GAME_ID,
      name: 'Apex Legends',
      icon: '/preview/session-chats/apex-mark.svg',
      logo: null,
    },
    teamLimit,
    voiceMode: scenario.mode,
    acceptedMembersCount: accepted.length,
    voiceConnectedCount: scenario.connected ? 1 : 0,
    voiceRoomStatus: scenario.mode === 'PLATFORM' ? 'OPEN' : null,
    chatId: scenario.noChat ? null : CHAT_ID,
    unreadCount: scenario.noChat ? 0 : 2,
    lastMessageText: scenario.noChat ? null : 'Заходите, я уже здесь',
    lastMessageAt: scenario.noChat ? null : minutesFromNow(-2),
    members: accepted.map((member) => ({
      memberId: member.memberId,
      status: member.status,
      displayName: member.userData.displayName,
      avatar: member.userData.avatar,
    })),
  };
  const records = scenario.archived
    ? [
        {
          ...session,
          status: 'COMPLETED',
          startedAt: minutesFromNow(-120),
          expiredAt: minutesFromNow(-60),
        },
      ]
    : scenario.future
      ? [
          {
            ...session,
            id: FUTURE_ID,
            ...(scenario.futureGuest ? { members: [host], acceptedMembersCount: 1 } : {}),
            sessionChat: scenario.futureGuest
              ? null
              : { id: 'cccccccc-cccc-4ccc-8ccc-cccccccccccc', unreadCount: 2 },
            voiceRoom: null,
            status: 'UPCOMING',
            startedAt: minutesFromNow(1440),
            expiredAt: minutesFromNow(1500),
          },
        ]
      : [];
  return { activeSession, viewerId, shouldShowRoom, room, records };
};

const rejectFixtureNetwork = async (): Promise<never> => {
  throw new Error('Session comparison fixtures cannot access the network');
};

// A separate client keeps fixture identities out of the authenticated application's cache.
// Force the policy after hook-specific options, which may explicitly request polling.
const createFixtureQueryClient = (renewal?: string, voiceAction?: string) => {
  let attempts = 0;
  const client = new QueryClient();
  const queryOptions = client.defaultQueryOptions.bind(client);
  const mutationOptions = client.defaultMutationOptions.bind(client);
  client.defaultQueryOptions = (options) => {
    const resolved = queryOptions(options);
    return {
      ...resolved,
      enabled: false,
      staleTime: Infinity,
      gcTime: Infinity,
      retry: false,
      refetchInterval: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      queryFn: (renewal && options.queryKey?.[0] === 'post-match-alerts'
        ? async () => client.getQueryData(options.queryKey!) ?? null
        : rejectFixtureNetwork) as typeof resolved.queryFn,
    };
  };
  client.defaultMutationOptions = (options) => ({
    ...mutationOptions(options),
    mutationFn:
      voiceAction && options?.mutationKey?.[0] === 'enable-teamplay-voice'
        ? async () => {
            if (voiceAction === 'loading') return new Promise(() => {});
            if (voiceAction === 'error') throw new Error('Fixture voice error');
            const contextKey = ['session-chat-context', POST_ID, SESSION_ID];
            const context = client.getQueryData<Record<string, unknown>>(contextKey);
            if (context) client.setQueryData(contextKey, { ...context, voiceMode: 'PLATFORM' });
            const rooms = client.getQueryData<SessionRoomEntry[]>(sessionRoomSessionsQueryKey);
            if (rooms)
              client.setQueryData(
                sessionRoomSessionsQueryKey,
                rooms.map((room) => ({ ...room, voiceMode: 'PLATFORM' }))
              );
            return { __typename: 'Operation', status: 'OK' };
          }
        : renewal && options?.mutationKey?.[0] === 'bump-post'
          ? async () => {
              const key = ['posts', 'home-preview-mine', HOST_ID];
              const mine = client.getQueryData<{ records: Array<Record<string, unknown>> }>(key);
              if (mine)
                client.setQueryData(key, {
                  ...mine,
                  records: mine.records.map((post) => ({
                    ...post,
                    bumpedAt: new Date().toISOString(),
                  })),
                });
              return { __typename: 'Operation', status: 'OK' };
            }
          : renewal && options?.mutationKey?.[0] === 'renew-post-match-alerts'
            ? async () => {
                if (renewal === 'loading') return new Promise(() => {});
                if (renewal === 'error' && attempts++ === 0)
                  throw new Error('Fixture renewal error');
                client.setQueryData(['post-match-alerts', POST_ID], {
                  matchAlertsEnabled: true,
                  matchAlertsExpiresAt: minutesFromNow(7 * 24 * 60),
                });
                return { __typename: 'Operation', status: 'OK' };
              }
            : rejectFixtureNetwork,
    retry: false,
  });
  return client;
};

const seedFixture = (scenario: Scenario, href: string, renewal?: string, voiceAction?: string) => {
  const snapshot = fixture(scenario);
  const { viewerId, shouldShowRoom, room, records } = snapshot;
  const queryClient = createFixtureQueryClient(renewal, voiceAction);
  for (const provider of ['STEAM', 'DISCORD']) {
    const queryKey = [
      'oauth-redirect-link',
      provider,
      new URL(`${envConfig.HOST}${href}`).toString(),
    ];
    if (provider === 'STEAM' && scenario.statsConnection) {
      queryClient
        .getQueryCache()
        .build(queryClient, { queryKey })
        .setState({
          status: scenario.statsConnection === 'error' ? 'error' : 'pending',
          fetchStatus: scenario.statsConnection === 'loading' ? 'fetching' : 'idle',
          error:
            scenario.statsConnection === 'error' ? new Error('Preview connection error') : null,
        });
    } else {
      queryClient.setQueryData(queryKey, 'https://example.invalid/preview-oauth');
    }
  }
  const candidate = {
    id: CANDIDATE_ID,
    gameId: GAME_ID,
    userData: {
      id: CANDIDATE_ID,
      displayName: 'pathfinder_77',
      avatar: '',
      lastVisit: minutesFromNow(-20),
      gender: Gender.MALE,
    },
    platformsIds: [PLATFORM_ID],
    languages: [{ code: 'ru', name: 'Русский' }],
    mic: true,
    text: 'Ищу команду на вечер',
    data: [],
  };
  queryClient.setQueryData(
    ['me'],
    scenario.anonymous
      ? null
      : {
          __typename: 'CurrentUser',
          id: viewerId,
          displayName:
            viewerId === HOST_ID ? 'artevard131' : viewerId === MEMBER_ID ? 'Lily' : 'Guest',
          avatar: '',
          timeZoneOffset: 3,
          timeFormat: '24h',
          emailConfirmed: true,
          verified: true,
          onboarding: { profileSaved: true, postCreated: true },
          statsVisibility:
            scenario.stats === 'hidden'
              ? { games: { [GAME_ID]: false } }
              : scenario.stats === 'account-hidden'
                ? { STEAM: false }
                : null,
          oauth: {
            steam: scenario.stats ? { id: 'preview-steam' } : null,
            discord:
              scenario.mode === 'DISCORD' ? { id: 'preview-discord', displayName: 'Lily' } : null,
          },
        }
  );
  if (scenario.large) {
    for (const member of snapshot.activeSession?.members ?? []) {
      if (!member?.memberId) continue;
      const seed = member.userData?.displayName ?? member.memberId;
      queryClient.setQueryData(['gameActivity', seed, GAME_ID], {
        playingNow: true,
        gameId: GAME_ID,
        currentGameName: 'Counter-Strike 2: Premier Competitive Edition',
        currentSource: 'STEAM',
      });
    }
  }
  queryClient.setQueryData(
    ['games'],
    [
      {
        id: GAME_ID,
        name: scenario.stats === 'private' ? 'Dota 2' : 'Apex Legends',
        slug: scenario.stats === 'private' ? 'dota-2' : 'apex-legends',
        desktopImage: '/game-bg/apex-legends.jpg',
        mobileImage: '/game-bg/apex-legends.jpg',
        session: true,
        providers: [],
      },
    ]
  );
  queryClient.setQueryData(['statsCollection', viewerId], {
    user: {
      me: {
        __typename: 'CurrentUser',
        statsCollection:
          scenario.stats === 'private' ? [{ provider: 'STEAM', state: 'PROFILE_PRIVATE' }] : [],
      },
    },
  });
  queryClient.setQueryData(
    ['platforms'],
    [{ id: PLATFORM_ID, name: scenario.statsPlatform ?? 'PC' }]
  );
  queryClient.setQueryDefaults(['post-sessions', POST_ID], { staleTime: Infinity });
  queryClient.setQueryData(['post-sessions', POST_ID], {
    pages: [{ records, hasMore: false, totalCount: records.length }],
    pageParams: [0],
  });
  queryClient.setQueryData(['post-session-history', POST_ID], {
    records,
    hasMore: false,
    totalCount: records.length,
  });
  queryClient.setQueryDefaults(['discordRoom', POST_ID], { staleTime: Infinity });
  queryClient.setQueryData(
    ['discordRoom', POST_ID],
    scenario.mode === 'DISCORD' && !scenario.discordPending
      ? {
          channelId: 'dddddddd-dddd-4ddd-8ddd-dddddddddddd',
          sessionId: SESSION_ID,
          postId: POST_ID,
          channelInviteLink: 'preview',
          channelName: 'Apex Legends',
          channelUserLimit: 3,
          published: true,
          currentStatusAction: 'ACCEPTED',
          discordChannelId: 'preview-channel',
          expiredAt: minutesFromNow(42),
          members: [],
          realtimeMembers: [],
        }
      : null
  );
  queryClient.setQueryData(['comments', POST_ID], {
    pages: [{ records: [], hasMore: false }],
    pageParams: [0],
  });
  queryClient.setQueryDefaults(['comments', POST_ID], { staleTime: Infinity });
  queryClient.setQueryData(
    ['recommended-players', GAME_ID, viewerId, 'same-platform', [PLATFORM_ID]],
    { records: [candidate] }
  );
  queryClient.setQueryData(['recommended-players', GAME_ID, viewerId], { records: [candidate] });
  queryClient.setQueryData(['follow-states', [CANDIDATE_ID]], new Map());
  queryClient.setQueryData(['recent-teammates', 'picker'], []);
  queryClient.setQueryData(voiceRoomQueryKey(SESSION_ID), {
    sessionId: SESSION_ID,
    postId: POST_ID,
    status: 'OPEN',
    hostId: HOST_ID,
    maxParticipants: 3,
    connectedCount: scenario.connected ? 1 : 0,
    participants: scenario.connected
      ? [{ userId: HOST_ID, displayName: 'artevard131', avatar: '', micEnabled: true }]
      : [],
    livekitUrl: null,
    token: null,
  });
  queryClient.setQueryData(['session-chat-context', POST_ID, SESSION_ID], {
    postId: POST_ID,
    gameId: GAME_ID,
    hostId: HOST_ID,
    sessionId: SESSION_ID,
    status: room.status,
    startedAt: room.startedAt,
    expiredAt: room.expiredAt,
    teamLimit: room.teamLimit,
    voiceMode: room.voiceMode,
    members: room.members,
  });
  queryClient.setQueryData(sessionRoomSessionsQueryKey, shouldShowRoom ? [room] : []);
  if (renewal) {
    queryClient.setQueryData(['post-match-alerts', POST_ID], {
      matchAlertsEnabled: renewal === 'active',
      matchAlertsExpiresAt:
        renewal === 'off' ? null : minutesFromNow(renewal === 'active' ? 10080 : -60),
    });
    const ownPost = {
      __typename: 'Post',
      id: POST_ID,
      userId: HOST_ID,
      gameId: GAME_ID,
      platformsIds: [PLATFORM_ID],
      languages: [{ code: 'ru', name: 'Русский' }],
      text: 'Ищу команду на вечер, с голосом и без токсичности.',
      mic: true,
      qr: false,
      createdAt: minutesFromNow(-7 * 24 * 60),
      bumpedAt: minutesFromNow(-72 * 60),
      updatedAt: minutesFromNow(-60),
      commentsCount: 0,
      reactionsCount: 0,
      favourite: false,
      adult: false,
      data: [],
      userData: {
        id: HOST_ID,
        displayName: 'artevard131',
        avatar: '',
        lastVisit: minutesFromNow(-8),
        gender: Gender.MALE,
        socials: [],
      },
      activeSession: snapshot.activeSession,
      gameData: { name: 'Apex Legends' },
      discordVoiceChannel: null,
    };
    queryClient.setQueryData(['posts', 'home-preview-mine', HOST_ID], { records: [ownPost] });
    queryClient.setQueryData(['my-sessions', 'home-preview'], {
      records: snapshot.activeSession ? [ownPost] : [],
    });
    queryClient.setQueryData(['discover-sessions', 'home-preview'], { records: [] });
    queryClient.setQueryData(['posts', 'home-preview-same-game', GAME_ID], { records: [] });
    queryClient.setQueryData(['feed', 'home-preview'], { records: [] });
    queryClient.setQueryData(['acquaintances'], []);
    queryClient.setQueryData(['playersByGame'], {
      pages: [{ items: [], nextCursor: null }],
      pageParams: [null],
    });
    queryClient.setQueryData(['chats', 30, 0, 'ALL'], {
      chats: [],
      hasMore: false,
      totalCount: 0,
    });
  }
  return { queryClient, snapshot };
};

export const Route = createFileRoute('/{-$locale}/_layout/preview/session-compare')({
  ssr: false,
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: zodValidator(searchSchema),
  loaderDeps: ({ search }) => ({
    state: search.state,
    design: search.design,
    previewLocale: search.previewLocale,
  }),
  loader: async ({ location, deps, params }) => {
    const previewLocale = deps.previewLocale ?? (params.locale === 'ru' ? 'ru' : 'en');
    const { messages } =
      previewLocale === 'ru'
        ? await import('#features/i18n/locales/ru/messages.po')
        : await import('#features/i18n/locales/en/messages.po');
    return { href: location.href, messages, previewLocale };
  },
  component: SessionCompareRoot,
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
});

function SessionCompareRoot() {
  const { state, renewal, voiceAction } = Route.useSearch();
  const { href, messages, previewLocale } = Route.useLoaderData();
  const scenario = SCENARIOS.find((item) => item.id === state) ?? SCENARIOS[0];
  return (
    <IsolatedSessionCompare
      key={`${scenario.id}:${previewLocale}:${renewal}:${voiceAction}`}
      scenario={scenario}
      renewal={renewal}
      voiceAction={voiceAction}
      href={href}
      messages={messages}
      previewLocale={previewLocale}
    />
  );
}

function IsolatedSessionCompare({
  scenario,
  renewal,
  voiceAction,
  href,
  messages,
  previewLocale,
}: {
  scenario: Scenario;
  renewal?: string;
  voiceAction?: string;
  href: string;
  messages: Messages;
  previewLocale: string;
}) {
  const [previewI18n] = useState(() =>
    setupI18n({ locale: previewLocale, messages: { [previewLocale]: messages } })
  );
  const [{ queryClient, snapshot }] = useState(() =>
    seedFixture(scenario, href, renewal, voiceAction)
  );
  useEffect(() => {
    const previousDeletedIds = useActiveChatStore.getState().deletedChatIds;
    const previousMode = useSessionRoomStore.getState().mode;
    useActiveChatStore.setState({ deletedChatIds: scenario.roomHidden ? [CHAT_ID] : [] });
    useSessionRoomStore.getState().setMode('mini');
    return () => {
      useActiveChatStore.setState({ deletedChatIds: previousDeletedIds });
      useSessionRoomStore.getState().setMode(previousMode);
    };
  }, [scenario.roomHidden]);
  return (
    <I18nProvider i18n={previewI18n}>
      <QueryClientProvider client={queryClient}>
        <LazyMotion features={domAnimation}>
          <MainLayout>
            <VoiceRoomProvider>
              <SessionCompare scenario={scenario} snapshot={snapshot} />
              <SessionRoomHost />
            </VoiceRoomProvider>
          </MainLayout>
        </LazyMotion>
      </QueryClientProvider>
    </I18nProvider>
  );
}

function SessionCompare({
  scenario,
  snapshot,
}: {
  scenario: Scenario;
  snapshot: ReturnType<typeof fixture>;
}) {
  const { design = 'current', surface, previewLocale, renewal, activity } = Route.useSearch();
  const renewOffer = useMatchAlertsRenewal(POST_ID);
  const [bumped, setBumped] = useState(false);
  const { activeSession, viewerId } = snapshot;
  const desktop = useBreakpointValue({ base: false, md: true }) ?? false;
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent =
      '[data-scope="toast"][id="toast:network-error"]{display:none!important}' +
      (design === 'proposed'
        ? '@media(max-width:379px){header a:has(>sup){width:140px!important}header a:has(>sup)>svg{max-width:100%;height:auto}}'
        : '');
    document.head.append(style);
    return () => style.remove();
  }, [design]);
  useEffect(() => {
    document.addEventListener('click', blockFixtureAction, true);
    document.addEventListener('submit', blockFixtureAction, true);
    return () => {
      document.removeEventListener('click', blockFixtureAction, true);
      document.removeEventListener('submit', blockFixtureAction, true);
    };
  }, []);

  const props: PostProps = {
    sessionDesign: design,
    game: {
      id: GAME_ID,
      name: 'Apex Legends',
      desktopImage: '/game-bg/apex-legends.jpg',
      mobileImage: '/game-bg/apex-legends.jpg',
      session: true,
      providers: [],
    },
    userData: {
      id: HOST_ID,
      displayName: 'artevard131',
      lastVisit: minutesFromNow(-8),
      avatar: '',
      age: 29,
      gender: Gender.MALE,
      socials: [],
    },
    post: {
      id: POST_ID,
      gameId: GAME_ID,
      platformsIds: [PLATFORM_ID],
      favourite: false,
      languages: [{ name: 'Русский', code: 'ru' }],
      mic: scenario.mode !== 'NONE',
      updatedAt: minutesFromNow(-4),
      bumpedAt: minutesFromNow(-30),
      text:
        scenario.mode === 'NONE'
          ? 'Ищу команду на вечер. Играем спокойно, без голосового чата.'
          : 'Ищу команду на вечер. Играем спокойно, с голосом и без токсичности.',
      qr: false,
      data: [],
      activeSession,
      authorGameActivity:
        activity === 'playing'
          ? {
              playingNow: true,
              gameId: GAME_ID,
              currentGameName: 'Counter-Strike 2: Premier Competitive Edition',
              currentSource: 'STEAM',
            }
          : activity === 'recent'
            ? {
                playingNow: false,
                lastPlayedGameName: 'Apex Legends',
                lastPlayedAt: minutesFromNow(-60),
              }
            : null,
    },
    platforms: [{ id: PLATFORM_ID, name: scenario.statsPlatform ?? 'PC' }],
    tags: [],
    isOwnPost: scenario.role === 'host',
    updatedAgoInSeconds: 240,
    canBump: Boolean(renewal) && !bumped,
    updatedTime: Date.now(),
    postId: POST_ID,
    onBumpPostClick: renewal
      ? () => {
          setBumped(true);
          void renewOffer.checkAfterBump();
        }
      : noop,
    matchAlertsRenewal: renewOffer.shouldOffer ? (
      <MatchAlertsRenewalNotice postId={POST_ID} onDismiss={renewOffer.dismiss} />
    ) : null,
    isBumpPostLoading: false,
    isAuth: !scenario.anonymous,
    refreshPostData: noop,
    myId: viewerId,
    onStartChatClick: noop,
    isStartChatLoading: false,
  };

  if (surface === 'manage' && activeSession) {
    return (
      <DialogRoot open onOpenChange={noop}>
        <SessionDetailsDialog
          {...activeSession}
          teamLimit={activeSession.teamLimit ?? 3}
          members={(activeSession.members ?? []).flatMap((member) =>
            member?.memberId
              ? [
                  {
                    memberId: member.memberId,
                    status: member.status,
                    userData: member.userData ? { ...member.userData, socials: [] } : null,
                  },
                ]
              : []
          )}
          postId={POST_ID}
          sessionId={SESSION_ID}
          isOwnPost={scenario.role === 'host'}
          hostData={{ memberId: HOST_ID, displayName: 'artevard131', avatar: '', socials: [] }}
          hasDiscordRoom={scenario.mode === 'DISCORD'}
          game={props.game}
          onAction={noop}
          onClose={noop}
        />
      </DialogRoot>
    );
  }

  if (surface === 'card') {
    const card = {
      ...props.post,
      id: POST_ID,
      userId: HOST_ID,
      adult: false,
      data: [],
      languages: props.post.languages?.map((language) => ({ ...language, nativeName: null })),
      createdAt: props.post.updatedAt,
      discordVoiceChannel: null,
      gameData: { id: GAME_ID, name: props.game.name, logo: null, platformsIds: [PLATFORM_ID] },
      userData: { ...props.userData, birthday: null, country: null },
      commentsCount: 0,
      discordMembersCount: scenario.connected && scenario.mode === 'DISCORD' ? 1 : 0,
      discordPublished: scenario.mode === 'DISCORD',
    } as PostPreviewProps;
    return (
      <Box w="100%" maxW="520px" mx="auto" p={3}>
        <Text textStyle="caption" color="fg.muted" mb={3}>
          QA · {scenario.id}
        </Text>
        <PostPreview {...card} />
      </Box>
    );
  }

  return (
    <DashboardLayout isAuth={!scenario.anonymous}>
      <Box w="100%" maxW="1200px" mx="auto" px={{ base: 3, md: 0 }}>
        <Panel data-session-compare-controls variant="nested" px={4} py={3} mb={5}>
          <Stack gap={3}>
            <Text textStyle="section">Сессия на заявке · сравнение настоящих компонентов</Text>
            <Flex gap={2} flexWrap="wrap">
              {SCENARIOS.map((item) => (
                <Link
                  key={item.id}
                  to="."
                  search={{ state: item.id, design, previewLocale }}
                  reloadDocument
                >
                  <Text
                    textStyle="caption"
                    color={item.id === scenario.id ? 'fg.emphasized' : 'fg.muted'}
                  >
                    {item.title}
                  </Text>
                </Link>
              ))}
            </Flex>
            <Flex gap={4}>
              <Link to="." search={{ state: scenario.id, previewLocale, design: 'current' }}>
                <Text textStyle="strong" color={design === 'current' ? 'purple.solid' : 'fg.muted'}>
                  Сейчас
                </Text>
              </Link>
              <Link to="." search={{ state: scenario.id, previewLocale, design: 'proposed' }}>
                <Text
                  textStyle="strong"
                  color={design === 'proposed' ? 'purple.solid' : 'fg.muted'}
                >
                  Предлагается
                </Text>
              </Link>
            </Flex>
            <Text textStyle="caption" color="fg.muted">
              Данные примера. Компоненты страницы и комнаты настоящие; действия заблокированы.
            </Text>
          </Stack>
        </Panel>
        <Box>
          {surface === 'home' ? (
            <HomePreview />
          ) : desktop ? (
            <Desktop {...props} />
          ) : (
            <Mobile {...props} />
          )}
        </Box>
      </Box>
    </DashboardLayout>
  );
}
